'use client';

import classNames from 'classnames';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import Typography from '@/components/atoms/typography/typography';
import { FilterItem } from '@/components/organisms/filter-card-list/filter-card-list';
import {
  DateData,
  DurationOptions,
  LocationChangeParameters,
  LocationData,
  LocationGroup,
  PersonsData,
  Room,
  RoomsState,
  SearchBarWrapperSectionData,
  SearchBarWrapperSectionType,
} from '@/components/organisms/search-bar/search-bar.types';
import {
  extractAdultCount,
  extractAdultsPerRoom,
  extractAutomaticRoomAssignment,
  extractCheckedLocations,
  extractChildrenAges,
  extractChildrenAgesPerRoom,
  extractDepartureDate,
  extractDestinationRegionIds,
  extractDurationSecondary,
  extractHotelIds,
  extractMaxDepartureDate,
  extractTransportationMethod,
  generatePersonsSectionValueString,
  isPersonsSectionValid,
  mergeObjects,
} from '@/components/organisms/search-bar/shared/search-utils/search-utils';
import { AdaptDestinationHierarchy } from '@/integrations/content/content-destinations/destinations-hierarchy/destination-hierarchy.adapter';
import useQueryDestinationHierarchy from '@/integrations/content/content-destinations/destinations-hierarchy/destination-hierarchy.hooks';
import useQueryCampaignsAvailableDates from '@/integrations/offers/campaigns/available-dates/available-dates.hooks';
import { useI18n } from '@/libraries/contexts/i18n-context';
import { usePageDetailsContext } from '@/libraries/contexts/page-details-context/page-details-context';
import { useSearchContext } from '@/libraries/contexts/search-context';
import {
  campaignPageEvent$,
  CampaignPageEventType,
} from '@/libraries/events/page-events';
import { updateUrlWithParameters } from '@/libraries/formatters/update-url-with-parameters';
import useDateFormatter from '@/libraries/formatters/use-date-formatter/use-date-formatter';
import getCulture from '@/libraries/getters/get-culture';
import getLocale from '@/libraries/getters/get-locale';
import { getSearchParameters } from '@/libraries/getters/get-search-parameters';
import useUiEventOutsideHandler from '@/libraries/hooks/use-ui-event-outside';
import SearchBarWrapper from './search-bar-wrapper/search-bar-wrapper';
// eslint-disable-next-line css-modules/no-unused-class
import styles from './search-bar.module.scss';

export interface SearchDefaults {
  adultsNumber?: string;
  adultsPerRoom?: string;
  childrenAges?: string[];
  childrenAgesPerRoom?: string;
  departureDate?: Date;
  departureId?: string;
  destinationIds?: string[] | null;
  destinationRegionIds?: string[] | null;
  durationType?: Content.Components['PeriodType'] | null;
  maxDepartureDate?: Date;
}

export interface SearchBarCampaignProps {
  /** Indicates whether the search bar is only buttons to open the widget modal  */
  buttonMode?: boolean;
  /** The class name to apply */
  className?: string;
  /** Indicates whether the search bar is used in the search widget */
  isWidget?: boolean;
  /** Default values for the search bar */
  propDefaultValues?: SearchDefaults;
  /** The search settings */
  searchSettings: Content.Components['SearchSettingsDto'];
  /** Indicates weather the search should trigger a navigation to the search page (used when the search bar is used on a different page)*/
  shouldNavigate?: boolean;
  /** Indicates weather the search should trigger a refresh of the page with the updated url params */
  shouldRefresh?: boolean;
  /** The travel portal search settings */
  travelPortalSearchSettings?:
    | Content.Components['TravelPortalSearchSettingsDto']
    | undefined;
}

const SECTION_INDEX_DATE = 3;

export const SearchBarCampaign = forwardRef(
  (
    {
      buttonMode = false,
      className,
      isWidget = false,
      propDefaultValues,
      searchSettings,
      shouldNavigate = false,
      shouldRefresh = false,
    }: SearchBarCampaignProps,
    ref,
  ) => {
    const { t } = useI18n();
    const { dateFormatter } = useDateFormatter();
    const rootRef = useRef<HTMLDivElement>(null);
    const router = useRouter();
    const searchParams = useSearchParams();
    const pathname = usePathname();
    const prevPath = useRef<string | null>(null);
    const {
      activeSection,
      setActiveSection,
      setAdultsNo,
      setAdultsNoPerRoom,
      setAutomaticRoomAssignment,
      setCampaignAvailableDates,
      setChildrenAges,
      setChildrenAgesPerRoom,
      setDepartureDate,
      setHotelIds,
      setMaxDepartureDate,
      setDepartureIds,
      setDestinationIds,
      setDestinationRegionIds,
      setPeriodType,
      setTriggeredBy,
    } = useSearchContext();
    const locale = getLocale();

    const hasDefaultValue = (parameterName: string): boolean => {
      // Check if parameter is in searchParams
      const isInQuery = searchParams!.has(parameterName);

      // Check if parameter is in searchDefaults
      const isInDefaults = propDefaultValues
        ? Object.hasOwnProperty.call(propDefaultValues, parameterName) &&
          propDefaultValues[parameterName] !== undefined
        : false;

      return isInQuery || isInDefaults;
    };

    const setupDurationOptions = (): DurationOptions[] => {
      const newDurationOptions = searchSettings.availablePeriodTypes.map(
        option => {
          let isSelected: boolean = false;
          let rangeSize: number;

          const urlDefaultValues = getSearchParameters(searchParams!);
          const defaultValues = mergeObjects(
            propDefaultValues,
            urlDefaultValues,
          );

          const selectedDurationType = defaultValues.durationType;

          // TODO: Handle rangeSize issue (#2311) gracefully
          switch (option.value) {
            case 'week':
              rangeSize = 7;
              break;

            case 'twoWeeks':
              rangeSize = 14;
              break;

            case 'threeWeeks':
              rangeSize = 21;
              break;

            default: {
              rangeSize = 0;
            }
          }

          if (option.value === selectedDurationType) {
            isSelected = true;
          }

          return {
            headline: option.label,
            icon: 'Calendar' as IconName,
            id: option.value,
            isSelected,
            rangeSize,
            text: option.description,
          };
        },
      );

      return newDurationOptions;
    };

    const { brand } = usePageDetailsContext();
    const initialDurationOptions = setupDurationOptions();

    const [locationSection, setLocationSection] =
      useState<SearchBarWrapperSectionData>({
        dropdownData: {
          locations: [],
          transportationOptions: [],
        },
        id: 1,
        isLoading:
          hasDefaultValue('destinationIds') || hasDefaultValue('departureIds'),
        // TODO: replace with data from search settings when available
        label: t('items.campaign.searchBar.location.locationTitle'),
        placeholder: t('items.campaign.searchBar.location.locationPlaceholder'),
        type: SearchBarWrapperSectionType.LOCATIONS,
      });

    const initialRoomsState: RoomsState = {
      automaticRoomAssignment: searchSettings.automaticRoomAssignment,
      rooms: [
        {
          childrenAges:
            searchSettings?.selectedPaxDistribution?.childrenAges ?? [],
          numberOfAdults:
            searchSettings?.selectedPaxDistribution?.adultsNo ??
            searchSettings.defaultNumberOfAdultsPerRoom ??
            0,
          numberOfChildren:
            searchSettings?.selectedPaxDistribution?.childrenNo ?? 0,
        },
      ],
    };

    const [personsSection, setPersonsSection] =
      useState<SearchBarWrapperSectionData>({
        dropdownData: {
          adultThreshold: searchSettings.adultAgeThreshold,
          defaultAdultNumber: searchSettings.defaultNumberOfAdultsPerRoom,
          maxPersons: searchSettings.maxNumberOfAdults,
          maxRooms: searchSettings.maxNumberOfRooms,
          roomDistributionWarning: searchSettings.roomDistributionWarning,
          roomsState: initialRoomsState,
        },
        id: 2,
        isLoading:
          hasDefaultValue('adultsNumber') ||
          hasDefaultValue('childrenAges') ||
          hasDefaultValue('adultsPerRoom') ||
          hasDefaultValue('childrenAgesPerRoom'),
        // TODO: replace with data from search settings when available
        label: t('items.campaign.searchBar.persons.personsTitle'),
        placeholder: t('items.campaign.searchBar.persons.personsPlaceholder'),
        type: SearchBarWrapperSectionType.PERSONS,
        value: generatePersonsSectionValueString({
          roomsState: initialRoomsState,
          t,
        }),
      });

    const [dateSection, setDateSection] = useState<SearchBarWrapperSectionData>(
      {
        dropdownData: {
          availableDates: [],
          durationOptions: initialDurationOptions,
          maxDate: new Date(searchSettings.maxDepartureDate as string),
          minDate: new Date(searchSettings.minDepartureDate as string),
          rangeSelect: searchSettings.rangeSelect ?? false,
        },
        id: 3,
        isLoading: false,
        // TODO: replace with data from search settings when available
        label: t('items.campaign.searchBar.date.dateTitle'),
        placeholder: t('items.campaign.searchBar.date.datePlaceholder'),
        type: SearchBarWrapperSectionType.DATE,
      },
    );

    const generateLocationValue = ({
      locations,
      transportationOptions,
    }: {
      locations?: LocationGroup[];
      transportationOptions?: FilterItem[];
    }): string[] => {
      const activeTransports =
        transportationOptions
          ?.filter(option => option.isSelected && option.headline)
          .map(option => option.headline) || [];

      const activeLocations =
        locations
          ?.flatMap(group =>
            group.options.filter(
              option => option.checked && !option.disabled && option.label,
            ),
          )
          .map(option => option.label) || [];

      const hasTransports = activeTransports.length > 0;
      const hasLocations = activeLocations.length > 0;

      if (hasTransports) {
        const transportsString = activeTransports.join(', ');

        if (hasLocations) {
          return [
            `${transportsString} ${t('items.common.words.to')}`,
            ...(activeLocations as string[]),
          ];
        }

        return [
          `${transportsString} ${t('items.search.searchBar.location.toAllDestinations')}`,
        ];
      }

      return activeLocations as string[];
    };

    const getDuration = useCallback(() => {
      return extractDurationSecondary(dateSection);
    }, [dateSection]);

    const getTransportationMethod = useCallback(() => {
      return extractTransportationMethod(locationSection);
    }, [locationSection]);

    const getCheckedLocations = useCallback(() => {
      return extractCheckedLocations(locationSection);
    }, [locationSection]);

    const getHotelIds = useCallback(() => {
      return extractHotelIds({
        isWidget: false,
        locationSection,
      });
    }, [locationSection]);

    const getDestinationRegionIds = useCallback(() => {
      return extractDestinationRegionIds(locationSection);
    }, [locationSection]);

    const getAdultCount = useCallback(() => {
      return extractAdultCount(personsSection);
    }, [personsSection]);

    const getChildrenAges = useCallback(() => {
      return extractChildrenAges(personsSection);
    }, [personsSection]);

    const getAdultsPerRoom = useCallback(() => {
      return extractAdultsPerRoom(personsSection);
    }, [personsSection]);

    const getChildrenAgesPerRoom = useCallback(() => {
      return extractChildrenAgesPerRoom(personsSection);
    }, [personsSection]);

    const getAutomaticRoomAssignment = useCallback(() => {
      return extractAutomaticRoomAssignment(personsSection);
    }, [personsSection]);

    const getDepartureDate = useCallback(() => {
      return extractDepartureDate(dateSection);
    }, [dateSection]);

    const getMaxDepartureDate = useCallback(() => {
      return extractMaxDepartureDate(dateSection);
    }, [dateSection]);

    const { data: dataAvailableDates, isFetching: isFetchingAvailableDates } =
      useQueryCampaignsAvailableDates(
        {
          adultsNumber: getAdultCount()?.toString() ?? '',
          brand: brand,
          campaignId: searchSettings.campaignId?.toString() ?? '',
          childrenAges: getChildrenAges() ?? '',
          departureIds: getTransportationMethod() ?? '',
          destinationIds: getCheckedLocations() ?? '',
          durationType: getDuration() as Content.Components['PeriodType'],
        },
        {
          enabled: activeSection === SECTION_INDEX_DATE,
        },
      );

    const {
      data: dataDestinationHierarchy,
      isFetching: isFetchingDestinationHierarchy,
    } = useQueryDestinationHierarchy({
      brand: brand,
      campaignId: searchSettings.campaignId?.toString() ?? '',
      durationType: getDuration() as Content.Components['PeriodType'],
    });

    const updateDestinationsAndDepartures = () => {
      const urlDefaultValues = getSearchParameters(searchParams!);
      const defaultValues = mergeObjects(propDefaultValues, urlDefaultValues);

      const updateDestinationsAndDeparturesInLocationSection = (
        data: AdaptDestinationHierarchy,
      ) => {
        setLocationSection(previousSection => {
          // Departures
          const selectedDepartureId = defaultValues?.departureId;

          const updatedTransportationOptions = data.departures.map(
            departure => ({
              ...departure,
              isSelected: departure.id === selectedDepartureId,
            }),
          );

          // Destinations
          const selectedLocations = [
            ...(defaultValues?.destinationIds ?? []),
            ...(defaultValues?.destinationRegionIds ?? []),
          ];

          // filter locations based on transportation method
          const updatedLocations = data.destinations?.map(group => ({
            ...group,
            options: group.options.map(location => ({
              ...location,
              checked: selectedLocations.includes(location.id),
              disabled: location.filterIds?.length === 0,
            })),
          }));

          return {
            ...previousSection,
            dropdownData: {
              ...previousSection.dropdownData,
              locations: updatedLocations,
              transportationOptions: updatedTransportationOptions,
            } as LocationData,
            isLoading: false,
            value: generateLocationValue({
              locations: updatedLocations,
              transportationOptions: updatedTransportationOptions,
            }),
          };
        });
      };

      updateDestinationsAndDeparturesInLocationSection({
        departures: dataDestinationHierarchy?.departures as FilterItem[],
        destinations: dataDestinationHierarchy?.destinations as LocationGroup[],
      });
    };

    useEffect(() => {
      if (!dataDestinationHierarchy) return;

      updateDestinationsAndDepartures();

      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataDestinationHierarchy]);

    useEffect(() => {
      if (!dataAvailableDates?.result) return;

      setCampaignAvailableDates(dataAvailableDates.result as unknown as Date[]);

      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataAvailableDates]);

    const handleClickOutside = () => {
      if (!isWidget) {
        setActiveSection(-1);
      }
    };

    useUiEventOutsideHandler(rootRef, handleClickOutside);

    const handleClickSection = (sectionId: number) => {
      if (activeSection === sectionId) {
        setActiveSection(-1);
      } else {
        setActiveSection(sectionId);
      }
    };

    const handleClickClose = () => {
      setActiveSection(-1);
    };

    const handleClickNext = () => {
      setActiveSection(activeSection + 1);
    };

    const handleClickBack = () => {
      setActiveSection(activeSection - 1);
    };

    const handleChangeDuration = (durationId: string) => {
      let updatedOptions: DurationOptions[];

      setDateSection(previousSection => {
        updatedOptions = (
          (previousSection?.dropdownData as DateData)?.durationOptions ?? []
        ).map(option => ({
          ...option,
          isSelected: option.id === durationId,
        }));

        return {
          ...previousSection,
          dropdownData: {
            ...(previousSection.dropdownData as DateData),
            durationOptions: updatedOptions,
          },
        };
      });
    };

    const handleChangeTransportation = (transportationId: string) => {
      setLocationSection(previousSection => {
        const dropdownData = previousSection.dropdownData as LocationData;
        const selectedTransportationId = transportationId;
        const updatedTransportationOptions =
          dropdownData.transportationOptions.map(option => ({
            ...option,
            isSelected: option.id === selectedTransportationId,
          }));

        // filter locations based on transportation method
        const updatedLocations = dropdownData.locations?.map(group => ({
          ...group,
          options: group.options.map(location => ({
            ...location,
            checked: location.filterIds?.includes(
              parseInt(selectedTransportationId),
            )
              ? location.checked
              : false,
            disabled: !location.filterIds?.includes(
              parseInt(selectedTransportationId),
            ),
          })),
        }));

        return {
          ...previousSection,
          dropdownData: {
            ...(previousSection.dropdownData as LocationData),
            locations: updatedLocations,
            transportationOptions: updatedTransportationOptions,
          },
          value: generateLocationValue({
            locations: updatedLocations,
            transportationOptions: updatedTransportationOptions,
          }),
        };
      });
    };

    const handleChangeLocations = ({
      destinationRegions,
      destinations,
      hotels,
      locations,
    }: LocationChangeParameters) => {
      setLocationSection(previousSection => {
        return {
          ...previousSection,
          dropdownData: {
            ...(previousSection.dropdownData as LocationData),
            destinationRegionIds: destinationRegions,
            destinations: destinations,
            hotelIds: hotels,
            locations,
          },
          value: generateLocationValue({
            locations,
            transportationOptions: (
              previousSection.dropdownData as LocationData
            ).transportationOptions,
          }),
        };
      });
    };

    const handleChangePersons = (roomsState: RoomsState) => {
      const valueString = generatePersonsSectionValueString({ roomsState, t });

      setPersonsSection(previousSection => {
        return {
          ...previousSection,
          dropdownData: {
            ...(previousSection.dropdownData as PersonsData),
            roomsState: roomsState,
          },
          isLoading: false,
          value: valueString,
        };
      });
    };

    const handleChangeDate = (newDates: Date[]) => {
      let valueString = '';

      if (newDates.length === 2) {
        const formattedStartDate = dateFormatter(newDates[0]).format(
          'D. MMM YYYY',
        );
        const formattedEndDate = dateFormatter(newDates[1]).format(
          'D. MMM YYYY',
        );

        valueString = `${formattedStartDate} ${t('items.search.searchBar.date.to')} ${formattedEndDate}`;
      }

      setDateSection(previousSection => {
        return {
          ...previousSection,
          dropdownData: {
            ...(previousSection.dropdownData as DateData),
            selectedDate: newDates[0],
            selectedMaxDate: newDates[1],
          },
          isLoading: false,
          isNotified: false,
          value: valueString,
        };
      });
    };

    const updateRoomsStateFromUrl = () => {
      const urlDefaultValues = getSearchParameters(searchParams!);
      const defaultValues = mergeObjects(propDefaultValues, urlDefaultValues);

      let rooms = [
        {
          childrenAges:
            searchSettings?.selectedPaxDistribution?.childrenAges ?? [],
          numberOfAdults:
            searchSettings?.selectedPaxDistribution?.adultsNo ?? 0,
          numberOfChildren:
            searchSettings?.selectedPaxDistribution?.childrenNo ?? 0,
        },
      ] as Room[];

      const hasAdultsPerRoom = defaultValues.adultsPerRoom;
      const automaticRoomAssignment = !hasAdultsPerRoom;

      if (defaultValues.adultsNumber) {
        rooms[0].numberOfAdults = parseInt(defaultValues.adultsNumber, 10);
      }

      if (defaultValues.childrenAges) {
        rooms[0].childrenAges = defaultValues.childrenAges.map(age =>
          parseInt(age, 10),
        );
        rooms[0].numberOfChildren = rooms[0].childrenAges.length;
      }

      if (hasAdultsPerRoom) {
        const adultsArray = defaultValues.adultsPerRoom
          .split('|')
          .map((number: string) => parseInt(number, 10));

        rooms = adultsArray.map((numberOfAdults: number) => ({
          childrenAges: [],
          numberOfAdults,
          numberOfChildren: 0,
        }));
      }

      if (defaultValues.childrenAgesPerRoom) {
        const childrenAgesArray = defaultValues.childrenAgesPerRoom
          .split('|')
          .map(room =>
            room
              .split(',')
              .map(age => (isNaN(parseInt(age, 10)) ? null : parseInt(age, 10)))
              .filter(age => age !== null),
          );

        childrenAgesArray.forEach((childrenAges, index) => {
          if (rooms[index]) {
            rooms[index].childrenAges = childrenAges;
            rooms[index].numberOfChildren = childrenAges.length;
          }
        });
      }

      handleChangePersons({ automaticRoomAssignment, rooms });
    };

    const updateDateStateFromUrl = () => {
      const urlDefaultValues = getSearchParameters(searchParams!);
      const defaultValues = mergeObjects(propDefaultValues, urlDefaultValues);

      if (defaultValues.departureDate && defaultValues.maxDepartureDate) {
        handleChangeDate([
          defaultValues.departureDate,
          defaultValues.maxDepartureDate,
        ]);
      }
    };

    const handleClickSearch = () => {
      campaignPageEvent$.next({ type: CampaignPageEventType.SEARCH });

      const constructAndUpdateSearchUrl = () => {
        const adultsNumber = getAdultCount()?.toString() as string;
        const adultsPerRoom = getAdultsPerRoom() as string;
        const childrenAges = getChildrenAges() as string;
        const childrenAgesPerRoom = getChildrenAgesPerRoom() as string;
        const departureDate = getDepartureDate() as string;
        const departureIds = getTransportationMethod() as string;
        const destinationIds = getCheckedLocations() as string;
        const destinationRegionIds = getDestinationRegionIds();
        const durationType = getDuration() as string;
        const hotelIds = getHotelIds();
        const maxDepartureDate = getMaxDepartureDate() as string;

        const parameters = new URLSearchParams({
          batchSize: searchSettings.batchSize.toString(),
          durationType,
          locale: getCulture(locale),
          sortBy: searchSettings.selectedSortBy ?? 'websitePriority',
        });

        if (departureDate !== undefined && maxDepartureDate !== undefined) {
          parameters.append('departureDate', departureDate);
          parameters.append('maxDepartureDate', maxDepartureDate);
        }

        if (departureIds !== undefined) {
          parameters.append('departureIds', departureIds);
        }

        if (destinationIds !== undefined) {
          parameters.append('destinationIds', destinationIds);
        }

        if (destinationRegionIds !== undefined && destinationRegionIds !== '') {
          parameters.append('destinationRegionIds', destinationRegionIds);
        }

        if (getAutomaticRoomAssignment() && adultsNumber !== '0') {
          parameters.append('childrenAges', childrenAges);
          parameters.append('adultsNumber', adultsNumber);
        } else if (adultsPerRoom !== '0') {
          parameters.append('adultsPerRoom', adultsPerRoom);
          parameters.append('childrenAgesPerRoom', childrenAgesPerRoom);
        }

        if (hotelIds !== undefined && hotelIds !== '') {
          parameters.append('hotelIds', hotelIds);
        }

        const parametersObject: Record<string, any> = {};

        parameters.forEach((value, key) => {
          parametersObject[key] = value;
        });

        setTriggeredBy('searchButton');

        updateUrlWithParameters({
          newParameters: parametersObject,
          overwrite: true,
          redirectUrl: searchSettings.searchPageUrl,
          router,
          shouldNavigate,
          shouldRefresh,
        });
      };

      if (
        getDuration() === undefined ||
        !isPersonsSectionValid(personsSection)
      ) {
        return;
      }

      setActiveSection(-1);
      constructAndUpdateSearchUrl();
    };

    useImperativeHandle(ref, () => ({
      search: handleClickSearch,
    }));

    useEffect(() => {
      updateRoomsStateFromUrl();
      updateDateStateFromUrl();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams]);

    useEffect(() => {
      setDepartureIds(getTransportationMethod() as string);
      setDestinationIds(getCheckedLocations() as string);
      setDestinationRegionIds(getDestinationRegionIds());
      setHotelIds(getHotelIds());
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [locationSection]);

    useEffect(() => {
      const childrenAges = getChildrenAges() as string;
      const adultsNumber = getAdultCount()?.toString() as string;
      const adultsPerRoom = getAdultsPerRoom() as string;
      const childrenAgesPerRoom = getChildrenAgesPerRoom() as string;

      if (getAutomaticRoomAssignment()) {
        setAutomaticRoomAssignment(true);
        setChildrenAges(childrenAges);
        setAdultsNo(adultsNumber);
        setAdultsNoPerRoom('');
        setChildrenAgesPerRoom('');
      } else {
        setAutomaticRoomAssignment(false);
        setAdultsNo('');
        setChildrenAges('');
        setAdultsNoPerRoom(adultsPerRoom);
        setChildrenAgesPerRoom(childrenAgesPerRoom);
      }

      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [personsSection]);

    useEffect(() => {
      setDepartureDate(getDepartureDate() as string);
      setMaxDepartureDate(getMaxDepartureDate() as string);
      setPeriodType(getDuration() as string);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dateSection]);

    // automatic trigger search
    useEffect(() => {
      const resetSearchBarState = () => {
        const initialDurationOptions = setupDurationOptions();

        if (dataDestinationHierarchy) {
          updateDestinationsAndDepartures();
        }

        updateRoomsStateFromUrl();

        setDateSection(previousSection => {
          return {
            ...previousSection,
            dropdownData: {
              ...(previousSection.dropdownData as DateData),
              availableDates: [],
              durationOptions: initialDurationOptions,
              maxDate: new Date(searchSettings.maxDepartureDate as string),
              minDate: new Date(searchSettings.minDepartureDate as string),
              rangeSelect: searchSettings.rangeSelect ?? false,
              selectedDate: undefined,
              selectedMaxDate: undefined,
            },
            isLoading: false,
            isNotified: false,
            value: '',
          };
        });
      };

      const handleAutomaticSearchTrigger = () => {
        const constructAndUpdateSearchUrl = () => {
          const parameters = new URLSearchParams({
            batchSize: searchSettings.batchSize.toString(),
            durationType:
              searchSettings.selectedPeriodType as Content.Components['PeriodType'],
            locale: getCulture(locale),
            sortBy: searchSettings.selectedSortBy ?? 'websitePriority',
          });

          const parametersObject: Record<string, any> = {};

          parameters.forEach((value, key) => {
            parametersObject[key] = value;
          });

          setTriggeredBy('automaticTrigger');

          updateUrlWithParameters({
            newParameters: parametersObject,
            overwrite: false,
            router,
          });
        };

        setActiveSection(-1);
        constructAndUpdateSearchUrl();
        resetSearchBarState();
      };

      const currentPath = (pathname ?? '').split('?')[0];

      if (prevPath.current !== currentPath) {
        prevPath.current = currentPath;
        handleAutomaticSearchTrigger();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pathname]);

    return (
      <div
        ref={rootRef}
        className={classNames(styles.component, styles.isCampaign, className)}
      >
        <div className={styles.searchBarLabel}>
          <Typography tag="label" tagStyle="labelMedium">
            {t('items.campaign.searchBar.searchBarLabel')}
          </Typography>
        </div>

        <SearchBarWrapper
          activeSection={activeSection}
          buttonLabel={t('items.campaign.searchBar.searchBarButton')}
          buttonMode={buttonMode}
          canSubmit
          isInCampaign
          isLoadingDate={isFetchingAvailableDates}
          isLoadingLocations={isFetchingDestinationHierarchy}
          isWidget={isWidget}
          onChangeDate={handleChangeDate}
          onChangeDuration={handleChangeDuration}
          onChangeLocations={handleChangeLocations}
          onChangePersons={handleChangePersons}
          onChangeTransportation={handleChangeTransportation}
          onClickBack={handleClickBack}
          onClickClose={handleClickClose}
          onClickNext={handleClickNext}
          onClickSearch={handleClickSearch}
          onClickSection={handleClickSection}
          searchSettings={searchSettings}
          sections={[locationSection, personsSection, dateSection]}
        />
      </div>
    );
  },
);

SearchBarCampaign.displayName = 'SearchBarCampaign';
